import React, { useState, useEffect, useCallback } from 'react';

/**
 * NotificationSettings — manages all notification preferences
 * 
 * Stores in localStorage as 'spark-notifications' JSON.
 * Other components read prefs via getNotificationPrefs() export.
 */

const DEFAULT_PREFS = {
  matches: true,
  likes: true,
  messages: true,
  dateProposals: true,
  dateUpdates: true,
  sounds: true,
  quietHoursEnabled: false,
  quietHoursStart: '23:00',
  quietHoursEnd: '07:00',
};

// ─── Public helper — importable by App.js or anywhere ───────────────
export function getNotificationPrefs() {
  try {
    const stored = localStorage.getItem('spark-notifications');
    if (stored) return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
  } catch {}
  return { ...DEFAULT_PREFS };
}

export function isQuietHoursActive(prefs) {
  if (!prefs.quietHoursEnabled) return false;
  const now = new Date();
  const currentMins = now.getHours() * 60 + now.getMinutes();
  const [startH, startM] = prefs.quietHoursStart.split(':').map(Number);
  const [endH, endM] = prefs.quietHoursEnd.split(':').map(Number);
  const startMins = startH * 60 + startM;
  const endMins = endH * 60 + endM;

  // Handle overnight ranges (e.g. 23:00 → 07:00)
  if (startMins > endMins) {
    return currentMins >= startMins || currentMins < endMins;
  }
  return currentMins >= startMins && currentMins < endMins;
}

/**
 * shouldNotify — check if a specific notification type should fire
 * @param {'matches'|'likes'|'messages'|'dateProposals'|'dateUpdates'} type
 * @returns {boolean}
 */
export function shouldNotify(type) {
  const prefs = getNotificationPrefs();
  if (!prefs[type]) return false;
  if (isQuietHoursActive(prefs)) return false;
  return true;
}

// ─── Toggle Switch Component ────────────────────────────────────────
function Toggle({ checked, onChange, label, description }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 0',
        cursor: 'pointer',
        borderBottom: '1px solid var(--border-light)',
        userSelect: 'none'
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: description ? '2px' : 0 }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{description}</div>
        )}
      </div>
      <div style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        background: checked ? 'var(--primary)' : 'var(--border)',
        position: 'relative',
        transition: 'background 0.2s',
        flexShrink: 0
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'white',
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        }} />
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    setPrefs(getNotificationPrefs());
  }, []);

  const update = useCallback((key, value) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem('spark-notifications', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return (
    <div>
      {/* ── Core notification toggles ── */}
      <Toggle
        checked={prefs.matches}
        onChange={(v) => update('matches', v)}
        label="💜 New Matches"
        description="When someone you liked likes you back"
      />
      <Toggle
        checked={prefs.likes}
        onChange={(v) => update('likes', v)}
        label="❤️ New Likes"
        description="When someone swipes right on you"
      />
      <Toggle
        checked={prefs.messages}
        onChange={(v) => update('messages', v)}
        label="💬 Messages"
        description="New messages from your matches"
      />
      <Toggle
        checked={prefs.dateProposals}
        onChange={(v) => update('dateProposals', v)}
        label="📅 Date Proposals"
        description="When someone proposes a date"
      />
      <Toggle
        checked={prefs.dateUpdates}
        onChange={(v) => update('dateUpdates', v)}
        label="📋 Date Updates"
        description="Accepted, declined, or cancelled dates"
      />

      {/* ── Divider ── */}
      <div style={{ height: '8px' }} />

      {/* ── Sound toggle ── */}
      <Toggle
        checked={prefs.sounds}
        onChange={(v) => update('sounds', v)}
        label="🔊 Sounds"
        description="Play a sound with notifications"
      />

      {/* ── Quiet Hours ── */}
      <Toggle
        checked={prefs.quietHoursEnabled}
        onChange={(v) => update('quietHoursEnabled', v)}
        label="🌙 Quiet Hours"
        description="Silence all notifications during set hours"
      />

      {prefs.quietHoursEnabled && (
        <div style={{
          background: 'var(--surface-dim)',
          borderRadius: 'var(--radius-md)',
          padding: '14px',
          marginTop: '8px',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>
              From
            </label>
            <input
              type="time"
              value={prefs.quietHoursStart}
              onChange={(e) => update('quietHoursStart', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '14px',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{
            color: 'var(--text-muted)',
            fontSize: '16px',
            paddingTop: '16px'
          }}>→</div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>
              Until
            </label>
            <input
              type="time"
              value={prefs.quietHoursEnd}
              onChange={(e) => update('quietHoursEnd', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '14px',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      )}

      {/* Status indicator */}
      {prefs.quietHoursEnabled && (
        <div style={{
          fontSize: '12px',
          color: 'var(--text-muted)',
          marginTop: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {isQuietHoursActive(prefs) ? (
            <><span style={{ color: 'var(--warning)' }}>●</span> Quiet hours active now</>
          ) : (
            <><span style={{ color: 'var(--success)' }}>●</span> Notifications active — quiet hours from {prefs.quietHoursStart} to {prefs.quietHoursEnd}</>
          )}
        </div>
      )}
    </div>
  );
}
